import React, { useState, useContext, useEffect } from "react";
import { AppContext } from "../../appContext";
import { sendReport } from "../../api/api";
import { getConsoleLogs } from "../../utils/consoleInterceptor";

import "./HotkeysModal.css";

interface IHotkeysModalProps {
    close: () => void;
    screenshot?: string;
    screenshotPromise?: Promise<string>;
}

export const HotkeysModal = ({ close, screenshot, screenshotPromise }: IHotkeysModalProps) => {
    const { translate } = useContext(AppContext);
    const [message, setMessage] = useState("");
    const [currentScreenshot, setCurrentScreenshot] = useState<string | undefined>(screenshot);
    const [screenshotLoading, setScreenshotLoading] = useState(false);
    const [isSendingReport, setIsSendingReport] = useState(false);

    // Handle dynamic screenshot loading
    useEffect(() => {
        if (screenshotPromise && !currentScreenshot) {
            setScreenshotLoading(true);
            screenshotPromise
                .then((screenshotData) => {
                    console.log('Screenshot loaded successfully');
                    setCurrentScreenshot(screenshotData);
                    setScreenshotLoading(false);
                })
                .catch((error) => {
                    console.warn('Screenshot loading failed:', error);
                    setScreenshotLoading(false);
                });
        }
    }, [screenshotPromise, currentScreenshot]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            close();
        }
    };

    const handleSendReport = async () => {
        if (message.trim() && !isSendingReport) {
            setIsSendingReport(true);
            try {
                const logs = getConsoleLogs();
                const response = await sendReport(message, logs, currentScreenshot);
                if (response.ok) {
                    console.log("Report sent successfully");
                    setMessage("");
                    close();
                } else {
                    console.error("Failed to send report:", response.statusText);
                }
            } catch (error) {
                console.error("Error sending report:", error);
            } finally {
                setIsSendingReport(false);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            close();
        }
    };

    return (
        <div className="hotkeysModal_backdrop" onClick={handleBackdropClick} onKeyDown={handleKeyDown}>
            <div className="hotkeysModal">
                <div className="hotkeysModal_header">
                    <h2>{translate('report_title')}</h2>
                    <button className="hotkeysModal_close" onClick={close}>×</button>
                </div>
                <div className="hotkeysModal_content">
                    <div className="hotkeysModal_messageSection">
                        <textarea
                            className="hotkeysModal_messageInput"
                            placeholder={translate('hotkeys_message_placeholder')}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={6}
                        />
                        
                        {screenshotLoading && (
                            <div className="hotkeysModal_screenshotStatus">
                                📸 {translate('capturing_screenshot') || 'Capturing screenshot...'}
                            </div>
                        )}
                        
                        {currentScreenshot && (
                            <div className="hotkeysModal_screenshotPreview">
                                ✅ {translate('screenshot_attached') || 'Screenshot attached'}
                            </div>
                        )}
                        
                        {isSendingReport && (
                            <div className="hotkeysModal_sendingStatus">
                                📨 {translate('sending_report') || 'Sending report...'}
                            </div>
                        )}
                        
                        <button
                            className="hotkeysModal_sendButton"
                            onClick={handleSendReport}
                            disabled={!message.trim() || isSendingReport}
                        >
                            {isSendingReport 
                                ? (translate('sending') || 'Sending...') 
                                : (translate('hotkeys_send_report') || 'Send Report')
                            }
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};